import React from "react";
import { Route, Routes } from "react-router-dom";
import DashboardLayout from "../layouts/DashboardLayout";
import DashboardAdmin from "../pages/Dashboard/DashboardAdmin";
import BookingsDashboard from "../pages/Bookings/BookingsDashboard/BookingsDashboardPage";
import AllBookingsPage from "../pages/Bookings/AllBookings/AllBookingsPage";
import BookingDetailsPage from "../pages/Bookings/BookingDetails/BookingDetailsPage";
import AllPackage from "../pages/Bookings/BookingsPackage/AllPackage";
import AddPackage from "../pages/Bookings/BookingsPackage/AddPackage";
import AllSlots from "../pages/Bookings/BookingsSlot/AllSlots";
import AllQuestions from "../pages/Bookings/BookingQuestions/AllQuestions";
import AddQuestions from "../pages/Bookings/BookingQuestions/AddQuestions";
import BookingSettings from "../pages/Settings/BookingSetting/BookingSettings";
import LoginPage from "../pages/Login/LoginPage";
import TokenLoginPage from "../pages/Login/TokenLoginPage";
import PrivateRoute from "./PrivateRoute";
import AdminRoute from "./AdminRoute";
import { useLocation } from "react-router-dom";
import MobileHeader from "../components/Header/MobileHeader";
import AllLeads from "../pages/Leads/AllLeads/AllLeads";
import LeadsDashboard from "../pages/Leads/LeadsDarshboard/LeadsDashBoard";
import LeadsCapture from "../pages/Leads/LeadsCapture/LeadsCapture";
import LeadsDetails from "../pages/Leads/LeadsDetails/LeadsDetails";
import Coupons from "../pages/Coupons/Coupons";
import RevenueDashboard from "../pages/Revenue/RevenueDashboard/RevenueDashboard";
import AllTransactions from "../pages/Revenue/AllTransactions/AllTransactions";
import WorkshopCoupon from "../pages/Coupons/WorkshopCoupon";
import Dashboard from "../pages/Dashboard/Dashboard";
import Payout from "../pages/Payout/Payout";
import PayoutDetails from "../pages/Payout/PayoutDetails";
import AdminSettings from "../pages/Settings/adminSetting/AdminSettings";
import AIDashboard from "../pages/AI/AIDashboard/AIDashboard";
import AISessions from "../pages/AI/AISessions/AISessions";
import AISessionDetail from "../pages/AI/AISessionDetail/AISessionDetail";
import AIUsage from "../pages/AI/AIUsage/AIUsage";
import AIFeedback from "../pages/AI/AIFeedback/AIFeedback";
import AIPlayground from "../pages/AI/AIPlayground/AIPlayground";
import WorkshopsList from "../pages/Workshops/WorkshopsList";
import WorkshopForm from "../pages/Workshops/WorkshopForm";
import WorkshopDetails from "../pages/Workshops/WorkshopDetails";
import ApiKeys from "../pages/Settings/ApiKeys/ApiKeys";
import AdminUsers from "../pages/Admin/Users/AdminUsers";
import AdminPurchases from "../pages/Admin/Purchases/AdminPurchases";
import RecipesList from "../pages/Recipes/RecipesList";
import RecipeForm from "../pages/Recipes/RecipeForm";
import RecipeSetup from "../pages/Recipes/RecipeSetup";
import useAuth from "../Hooks/useAuth";

const MainRouter = () => {
  const location = useLocation(); // Hook to get the current route

  // List of routes where the header should not appear
  const excludedRoutes = ["/login", "/auth/token-login"];

  const shouldShowHeader =
    !excludedRoutes.includes(location.pathname) &&
    !location.pathname.startsWith("/auth/token-login");
  const { isAdmin } = useAuth();
  const adminOnly = (component) => <AdminRoute>{component}</AdminRoute>;

  return (
    <>
      {shouldShowHeader && <MobileHeader />}
      <Routes>
        {/* Public Route */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/token-login/:token" element={<TokenLoginPage />} />

        {/* Protected Routes */}
        <Route
          path="/"
          element={
            <PrivateRoute>
              <DashboardLayout />
            </PrivateRoute>
          }
        >
          {/* Dashboard Home */}
          {isAdmin && <Route index element={<DashboardAdmin />} />}
          {!isAdmin && <Route index element={<Dashboard />} />}
          {/* Renders for path="/" */}
          {/* Bookings Nested Routes */}
          <Route path="bookings">
            <Route path="dashboard" element={<BookingsDashboard />} />
            <Route path="all" element={<AllBookingsPage />} />
            <Route path="details/:id" element={<BookingDetailsPage />} />
            <Route path="packages" element={<AllPackage />} />
            <Route path="packages/add" element={<AddPackage />} />
            <Route path="slots" element={<AllSlots />} />
            <Route path="questions" element={<AllQuestions />} />
            <Route path="questions/add" element={<AddQuestions />} />
            <Route path="settings" element={<BookingSettings />} />
          </Route>
          <Route path="leads">
            <Route path="all" element={<AllLeads />} />
            <Route path="dashboard" element={<LeadsDashboard />} />
            {/* {/* <Route path="all" element={<AllLeads />} /> */}
            <Route path="leadscapture" element={<LeadsCapture />} />
            <Route path="details/:id" element={<LeadsDetails />} />
          </Route>
          <Route path="revenue">
            <Route path="all" element={<AllTransactions />} />
            <Route path="dashboard" element={<RevenueDashboard />} />
            {/* {/* <Route path="all" element={<AllLeads />} /> */}
            <Route path="leadscapture" element={<LeadsCapture />} />
            <Route path="details/:id" element={<LeadsDetails />} />
          </Route>
          <Route path="coupon">
            <Route path="workshop" element={<WorkshopCoupon />} />
            <Route path="bookings" element={<Coupons />} />
          </Route>
          <Route path="payout">
            <Route index element={<Payout />} />
            <Route path=":id" element={<PayoutDetails />} />
          </Route>
          <Route path="admin">
            <Route path="settings" element={adminOnly(<AdminSettings />)} />
            <Route path="users" element={adminOnly(<AdminUsers />)} />
            <Route path="purchases" element={adminOnly(<AdminPurchases />)} />
          </Route>
          <Route path="api-keys" element={adminOnly(<ApiKeys />)} />
          <Route path="ai">
            <Route path="dashboard" element={adminOnly(<AIDashboard />)} />
            <Route path="sessions" element={adminOnly(<AISessions />)} />
            <Route path="sessions/:sessionId" element={adminOnly(<AISessionDetail />)} />
            <Route path="usage" element={adminOnly(<AIUsage />)} />
            <Route path="feedback" element={adminOnly(<AIFeedback />)} />
            <Route path="playground" element={adminOnly(<AIPlayground />)} />
          </Route>
          <Route path="workshops">
            <Route index element={adminOnly(<WorkshopsList />)} />
            <Route path="new" element={adminOnly(<WorkshopForm />)} />
            <Route path=":id/edit" element={adminOnly(<WorkshopForm />)} />
            <Route path=":id" element={adminOnly(<WorkshopDetails />)} />
          </Route>
          <Route path="recipes">
            <Route index element={adminOnly(<RecipesList />)} />
            <Route path="new" element={adminOnly(<RecipeForm />)} />
            <Route path=":id/edit" element={adminOnly(<RecipeForm />)} />
            <Route path="setup" element={adminOnly(<RecipeSetup />)} />
          </Route>
        </Route>
      </Routes>
    </>
  );
};

export default MainRouter;
